// src/ai/queryCommands.js

import { db } from '../firebase';
import { collection, query, where, getDocs, Timestamp, collectionGroup } from 'firebase/firestore';
import { numberToTurkishWords, getTimeframeFromCommand } from './helpers';
import { startOfDay, endOfDay } from 'date-fns';

// Cihaz sahibini sorgulama
export const handleDeviceOwnerLookup = async (command) => {
    const serialNumberMatch = command.match(/(\w+)\s+seri numaralı cihaz/);
    if (serialNumberMatch && serialNumberMatch[1]) {
        const serialNumber = serialNumberMatch[1].toUpperCase();
        const q = query(collection(db, "devices"), where("seriNumarasi", "==", serialNumber));
        const snapshot = await getDocs(q);
        if (snapshot.empty) {
            return `${serialNumber} seri numarasına sahip bir cihaz bulunamadı.`;
        } else {
            const device = snapshot.docs[0].data();
            return `${serialNumber} seri numaralı cihaz, ${device.musteriAdSoyad} adına kayıtlıdır.`;
        }
    }
    return null;
};

// Müşteri telefonunu sorgulama
export const handleCustomerPhoneLookup = async (command, customers) => {
    const foundCustomer = customers.find(c => command.includes(c.adSoyad.toLowerCase()));
    if (foundCustomer) {
        return `${foundCustomer.adSoyad} isimli müşterinin telefon numarası ${foundCustomer.telefon}.`;
    }
    return "Müşteri bulunamadı, lütfen tam adını tekrar söyler misiniz?";
};

// Hizmet sayısını sorgulama
export const handleServiceCountQuery = async (command) => {
    const timeframe = getTimeframeFromCommand(command);
    if (!timeframe) return "Lütfen bir zaman aralığı belirtin: 'geçen ay', 'bu hafta' gibi.";
    
    const serviceKeywordMatch = command.match(/kaç tane ([\w\s]+) yapıldı/);
    if (serviceKeywordMatch && serviceKeywordMatch[1]) {
        const serviceKeyword = serviceKeywordMatch[1].trim();
        const q = query(collection(db, "serviceRecords"), where("olusturmaTarihi", ">=", timeframe.start), where("olusturmaTarihi", "<=", timeframe.end));
        const snapshot = await getDocs(q);
        
        let count = 0;
        snapshot.forEach(doc => {
            const record = doc.data();
            const hasServiceInAriza = record.arizaTanimi?.toLowerCase().includes(serviceKeyword);
            const hasServiceInParcalar = record.kullanilanParcalar?.some(p => p.aciklama.toLowerCase().includes(serviceKeyword));
            if(hasServiceInAriza || hasServiceInParcalar) count++;
        });

        return `${timeframe.name} içinde toplam ${numberToTurkishWords(count)} adet ${serviceKeyword} hizmeti verilmiştir.`;
    }
    return null;
};

// Müşteri cari bilgisini sorgulama
export const handleCustomerBalance = async (command, customers) => {
    const foundCustomer = customers.find(c => command.includes(c.adSoyad.toLowerCase()));
    if (foundCustomer) {
        const [recordsSnapshot, transactionsSnapshot, scheduledSnapshot] = await Promise.all([
            getDocs(query(collection(db, "serviceRecords"), where("musteriId", "==", foundCustomer.id))),
            getDocs(query(collection(db, "transactions"), where("customerId", "==", foundCustomer.id))),
            getDocs(query(collection(db, "scheduledPayments"), where("musteriId", "==", foundCustomer.id), where("durum", "==", "Bekleniyor")))
        ]);
        
        let totalRevenue = 0, totalPaid = 0;
        recordsSnapshot.forEach(doc => {
            totalRevenue += doc.data().toplamTutar || 0;
            totalPaid += doc.data().odenenTutar || 0;
        });
        transactionsSnapshot.forEach(doc => {
            if (doc.data().type === 'borc') totalRevenue += doc.data().amount || 0;
            else if (doc.data().type === 'tahsilat') totalPaid += doc.data().amount || 0;
        });

        const balance = totalRevenue - totalPaid;
        const balanceInWords = numberToTurkishWords(Math.abs(Math.round(balance)));
        let textToSpeak = `${foundCustomer.adSoyad} müşterisinin `;
        if (balance > 0) textToSpeak += `yaklaşık ${balanceInWords} türk lirası borcu bulunuyor.`;
        else if (balance < 0) textToSpeak += `yaklaşık ${balanceInWords} türk lirası alacağı bulunuyor.`;
        else textToSpeak += `borcu bulunmuyor.`;

        if (!scheduledSnapshot.empty) {
            const scheduledTotal = scheduledSnapshot.docs.reduce((sum, doc) => sum + (doc.data().tutar || 0), 0);
            textToSpeak += ` Ayrıca, ${numberToTurkishWords(scheduledSnapshot.size)} adet planlanmış tahsilatı mevcut, toplam tutarı ${numberToTurkishWords(Math.round(scheduledTotal))} türk lirası.`;
        }
        return textToSpeak;
    }
    return null;
};

// Servis durum özeti
export const handleServiceStatus = async () => {
    const activeStatuses = ["Beklemede", "İşlemde", "Tamamlandı", "Teslime Hazır"];
    const q = query(collection(db, "serviceRecords"), where("durum", "in", activeStatuses));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return "Şu anda serviste aktif bir cihaz bulunmuyor.";
    
    const statusCounts = { "Beklemede": 0, "İşlemde": 0, "Tamamlandı": 0, "Teslime Hazır": 0 };
    snapshot.forEach(doc => { if (doc.data().durum in statusCounts) statusCounts[doc.data().durum]++; });
    
    let textToSpeak = `Elbette, aktif servislerin genel durumu şöyle: `;
    const speakParts = Object.entries(statusCounts).filter(([, count]) => count > 0).map(([status, count]) => `${numberToTurkishWords(count)} cihaz ${status.toLowerCase()}`);
    textToSpeak += speakParts.join(", ") + ".";
    return textToSpeak;
};

// Ön görüşme özeti
export const handlePreInterviews = async () => {
    const activeStatuses = ["Yeni", "Aranacak", "Geri Arandı"];
    const q = query(collection(db, "preInterviews"), where("durum", "in", activeStatuses));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return "Takip edilecek aktif bir ön görüşme bulunmuyor.";

    const statusCounts = { "Yeni": 0, "Aranacak": 0, "Geri Arandı": 0 };
    snapshot.forEach(doc => { if (doc.data().durum in statusCounts) statusCounts[doc.data().durum]++; });

    let textToSpeak = `Toplam ${numberToTurkishWords(snapshot.size)} aktif ön görüşme var. `;
    const speakParts = Object.entries(statusCounts).filter(([, count]) => count > 0).map(([status, count]) => `${numberToTurkishWords(count)} kişi ${status.toLowerCase()} durumunda`);
    textToSpeak += speakParts.join(", ") + ".";
    return textToSpeak;
};

// GÜNCELLENDİ: Günlük rapor, daha sağlam veri kontrolü ile
export const handleDailyReport = async (command) => {
    const timeframe = getTimeframeFromCommand(command) || { start: startOfDay(new Date()), end: endOfDay(new Date()), name: "Bugün" };
    
    const [recordsSnapshot, transactionsSnapshot, recordPaymentsSnapshot, activeServicesSnapshot] = await Promise.all([
        getDocs(query(collection(db, "serviceRecords"), where("olusturmaTarihi", ">=", Timestamp.fromDate(timeframe.start)), where("olusturmaTarihi", "<=", Timestamp.fromDate(timeframe.end)))),
        getDocs(query(collection(db, "transactions"), where("timestamp", ">=", Timestamp.fromDate(timeframe.start)), where("timestamp", "<=", Timestamp.fromDate(timeframe.end)))),
        getDocs(query(collectionGroup(db, 'payments'), where('tarih', '>=', Timestamp.fromDate(timeframe.start)), where('tarih', '<=', Timestamp.fromDate(timeframe.end)))),
        getDocs(query(collection(db, "serviceRecords"), where("durum", "==", "İşlemde")))
    ]);

    const totalCiro = recordsSnapshot.docs.reduce((sum, doc) => sum + (doc.data().toplamTutar || 0), 0);
    const generalPaymentsTotal = transactionsSnapshot.docs.filter(t => t.data().type === 'tahsilat').reduce((sum, t) => sum + (t.data().amount || 0), 0);
    const recordPaymentsTotal = recordPaymentsSnapshot.docs.reduce((sum, p) => sum + (p.data().tutar || 0), 0);
    const totalTahsilat = generalPaymentsTotal + recordPaymentsTotal;
    
    return `${timeframe.name} yapılan ciro ${numberToTurkishWords(Math.round(totalCiro))} türk lirası. Yapılan tahsilat ${numberToTurkishWords(Math.round(totalTahsilat))} türk lirası. Şu anda ${numberToTurkishWords(activeServicesSnapshot.size)} cihaz serviste işlemde.`;
};